'use client'

import {useEffect, useRef, useState} from 'react'
import {HgButtonLink} from '~/design-system/hg/components'
import {TOC_ID} from '~/design-system/hg/components/HgTableOfContents'

export const TEXT_CONTENT_ID = 'text-content'

export default function ReturnToTableOfContentsButton() {
  const [tocVisible, setTocVisible] = useState(false)
  const [textContentVisible, setTextContentVisible] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const textContent = document.getElementById(TEXT_CONTENT_ID)
    const toc = document.getElementById(TOC_ID)
    if (!textContent || !toc) return

    const tocObserver = new IntersectionObserver(
      ([entry]) => {
        setTocVisible(entry.isIntersecting)
      },
      // account for blog navbar
      {
        rootMargin: '-120px 0px',
      }
    )

    const textContentObserver = new IntersectionObserver(
      ([entry]) => {
        setTextContentVisible(entry.isIntersecting)
      },
      {
        rootMargin: '-100% 0px',
      }
    )

    tocObserver.observe(toc)
    textContentObserver.observe(textContent)

    return () => {
      tocObserver.disconnect()
      textContentObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!buttonRef.current) return

    if (!tocVisible && textContentVisible) {
      // show the button
      buttonRef.current.removeAttribute('aria-hidden')
      buttonRef.current.removeAttribute('data-hidden')
    }
  }, [tocVisible, textContentVisible])

  return (
    <HgButtonLink
      href={`#${TOC_ID}`}
      className="fixed inset-x-0 bottom-32 m-auto animate-fadeIn opacity-0 duration-300 aria-hidden:hidden data-[hidden]:animate-fadeOut lg:left-32 lg:m-0"
      iconProps={{iconType: 'arrow-up', position: 'right'}}
      variant="elevated"
      data-hidden={tocVisible || (!tocVisible && !textContentVisible)}
      onAnimationEnd={e => {
        // data-hidden to trigger the fade out animation
        // aria-hidden to hide the button in the DOM after the animation finishes
        if (e.currentTarget.getAttribute('data-hidden') === 'true') {
          e.currentTarget.setAttribute('aria-hidden', 'true')
        }
      }}
      buttonRef={buttonRef}
    >
      Table of Contents
    </HgButtonLink>
  )
}
